import { defineRecipe, defineSemanticTokens, defineSlotRecipe } from '@chakra-ui/react';

import Colors from './Colors';

/**
 * BlackBoiler brand styling for Chakra/rocket — the successor to the
 * `overrides` block in `MaterialTheme.js`.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * `MaterialTheme.js` styles 32 MUI components app-wide from a file the
 * components never mention. Every rocket conversion silently drops that
 * styling, which is the real source of the bbcedit-vs-operahouse visual deltas.
 * Chakra's equivalent is recipes/slotRecipes + semanticTokens, and rocket
 * defines NONE today — hence conversions landing on raw Chakra defaults.
 *
 * ── PORTABILITY RULES (do not break these) ─────────────────────────────────
 * This module is a **plain config fragment**, not a `createSystem` call, so it
 * can be composed into either host system:
 *   - bbcedit     → ROCKET_THEME_LEGACY_ROOT (preflight off, rem tokens x1.6)
 *   - word-plugin → ROCKET_THEME             (preflight on, unscaled)
 * Hoisting this file into `@rocket/ui` should therefore be a file move plus an
 * export, with zero call-site changes in either app.
 *
 * 1. **Author sizes in `px`, NEVER raw `rem`.** MaterialTheme's rem values were
 *    written for bbcedit's `html { font-size: 62.5% }` (10px root), so `1.3rem`
 *    means 13px *here* but 20.8px in word-plugin's 16px root. px is
 *    root-independent, works in both, and survives Phase 7 killing the 62.5%
 *    root. (All rem values below are pre-converted: 1.3rem→13px, 1.5rem→15px.)
 * 2. **No `preflight` / no rem-scaling here.** Those belong to the host system
 *    wrapper, not to brand data.
 * 3. **Keep this opt-in when hoisted** — rocket-app and component-viewer should
 *    not silently inherit BlackBoiler brand.
 *
 * ── Dark mode ──────────────────────────────────────────────────────────────
 * `semanticTokens` below are the mechanism: every `_dark` value is declared so
 * the plumbing is real and one place drives the flip. The dark VALUES are
 * currently Chakra-sensible placeholders, NOT a designed BlackBoiler dark
 * palette — see BB_DARK_PALETTE_IS_PROVISIONAL. Do not ship dark mode on these
 * without design review.
 */

/** Dark values below are provisional placeholders pending design review. */
export const BB_DARK_PALETTE_IS_PROVISIONAL = true;

/**
 * Brand + neutral colours. Neutrals carry `_dark` so `bg`/`fg`/`border` flip
 * from one place; brand colours (BlackBoiler blue/red/green) deliberately keep
 * a single value in both modes until design says otherwise.
 */
const semanticTokens = defineSemanticTokens({
  colors: {
    // ── Neutrals: these are what dark mode actually flips ──
    bg: {
      DEFAULT: { value: { _light: Colors.trueWhite, _dark: '#1A1D21' } },
      subtle: { value: { _light: Colors.grey7, _dark: '#212529' } },
      muted: { value: { _light: Colors.pageBackground, _dark: '#25292E' } },
    },
    fg: {
      DEFAULT: { value: { _light: Colors.black, _dark: '#E7ECF0' } },
      muted: { value: { _light: Colors.grey2, _dark: '#9BA6B2' } },
      subtle: { value: { _light: Colors.grey5, _dark: '#7A8695' } },
    },
    border: {
      DEFAULT: { value: { _light: Colors.grey8, _dark: '#343A40' } },
      subtle: { value: { _light: Colors.grey1, _dark: '#2B3035' } },
    },

    // ── `blue.subtle` = the app-wide "selected / active" highlight surface
    // (nav items, selected rows, active playbook sections, expanded tenant row).
    // Chakra's default dark `blue.subtle` is a near-black navy that reads as
    // "not selected"; override it with a clearly-visible blue-grey. Light keeps
    // a soft blue tint. All 13 call sites want this exact semantic. ──
    // Dark value is deliberately desaturated: it has to work as a thin nav-item
    // highlight AND as a full-bleed expanded-row fill (TenantDashboard) without
    // shouting. Still clearly blue against bg.muted (#25292E).
    blue: {
      subtle: { value: { _light: '#E7F0FF', _dark: '#24344A' } },
    },

    // ── Brand / status: single value for now (design review before splitting) ──
    bb: {
      blue: { value: { _light: Colors.blue, _dark: Colors.blue } },
      red: { value: { _light: Colors.red, _dark: Colors.red } },
      green: { value: { _light: Colors.green, _dark: Colors.green } },
      /** MuiTabs.indicator / MuiTab.Mui-selected — the red tab underline. */
      tabAccent: { value: { _light: '#9E2B1D', _dark: '#C4523F' } },
    },
  },
});

/**
 * Button — ports MaterialTheme `typography.button`.
 * NOTE: bbcedit's palette sets `primary.main = Colors.blue` and defines NO
 * button gradient, so blue solid buttons are correct per bbcedit's own theme.
 * (The "buttons should be black gradient" note in the older handoff came from
 * operahouse, which is a different codebase.)
 *
 * ⚠️ VARIANTS BEAT BASE: Chakra applies variant styles after `base`, and the
 * default `size: "md"` variant sets `textStyle`/`px` — so fontSize and padding
 * MUST live in the size variant, not in `base`, or the default silently wins.
 */
const button = defineRecipe({
  base: {
    fontWeight: 700,
    minHeight: '40px',
    letterSpacing: '1px',
    borderRadius: '5px',
  },
  variants: {
    size: {
      // ponytail: only "md" (the default) carries brand metrics; brand the
      // other sizes here if bbcedit ever passes size= explicitly.
      md: {
        textStyle: 'none',
        fontSize: '13px', // was 1.3rem @ 62.5% root
        paddingInline: '24px',
      },
    },
  },
});

/**
 * Heading — ports MaterialTheme's `typography.h1..h4` family/color/SIZE so the
 * per-level scale lives in ONE place instead of a fontSize= on every call site.
 *
 * Sizes (px @ 62.5% root, verbatim from MaterialTheme): h1 24/700, h2 18/600,
 * h3 15/600, h4 13/700 uppercase + brand blue. h5/h6 had NO MUI size (they fell
 * back to browser defaults), so they're left to the call site.
 *
 * ⚠️ VARIANTS BEAT BASE (the rc29 trap): Chakra's default `size: "md"` variant
 * sets fontSize via `textStyle`, applied AFTER `base`. A flat `fontSize` in
 * `base` would lose. We beat it with **tag-attribute selectors** (`&:is(h1)`) —
 * an attribute/type selector outranks the variant's single flat class in CSS
 * specificity, so these win within the cascade layer without touching variants.
 * family + color stay unscoped in base (chakra's size variant sets neither).
 *
 * NOTE: px is deliberate (bbcedit's 62.5% root makes rem ambiguous vs
 * word-plugin's 16px root). Converting this scale px→rem for a11y is the
 * documented final close-out step, done once the 62.5% root is removed.
 */
const heading = defineRecipe({
  base: {
    fontFamily: 'Montserrat, sans-serif',
    color: 'fg',
    '&:is(h1)': { fontSize: '24px', fontWeight: 700, lineHeight: 1.2 },
    '&:is(h2)': { fontSize: '18px', fontWeight: 600, lineHeight: 1.2 },
    '&:is(h3)': { fontSize: '15px', fontWeight: 600, lineHeight: 1.2 },
    '&:is(h4)': {
      fontSize: '13px',
      fontWeight: 700,
      lineHeight: 1.2,
      textTransform: 'uppercase',
      color: 'bb.blue',
    },
  },
});

/**
 * Tabs — ports MuiTabs.indicator + MuiTab.Mui-selected (the red accent).
 *
 * ⚠️ VARIANTS BEAT BASE: the default `line` variant sets `_selected.color: "fg"`
 * and draws its underline via the `--indicator-color` CSS var (the `indicator`
 * slot never renders for `line`), so the accent must override the variant.
 * ponytail: only `line` (the default) is branded; brand subtle/enclosed here if
 * bbcedit ever passes variant= explicitly.
 */
const tabs = defineSlotRecipe({
  slots: ['root', 'list', 'trigger', 'indicator', 'content'],
  variants: {
    variant: {
      line: {
        trigger: {
          _selected: {
            color: 'bb.tabAccent',
            _horizontal: { '--indicator-color': 'colors.bb.tabAccent' },
            _vertical: { '--indicator-color': 'colors.bb.tabAccent' },
          },
        },
      },
    },
  },
});

/**
 * Slider — ports the `MuiSlider.colorSecondary` gradient scale (the blue→teal
 * "confidence" track used on the paragraph-insert threshold sliders). Adds a new
 * opt-in `variant: "gradient"` value; the default `outline`/`solid` sliders are
 * untouched, so only components that pass `variant="gradient"` get the scale.
 *
 * ⚠️ VARIANTS BEAT BASE: Chakra's slider fills the `range` slot from
 * `colorPalette.solid` in its default `outline`/`solid` variants, so the gradient
 * must live in a variant, not `base`. Merged over Chakra's recipe by createSystem.
 * The gradient's two stops are the exact MaterialTheme literals; kept inline here
 * (the theme layer is the single control surface) rather than scattered in a
 * component. ponytail: promote to a token only if a second consumer appears.
 */
const slider = defineSlotRecipe({
  slots: ['root', 'control', 'track', 'range', 'thumb', 'marker'],
  variants: {
    variant: {
      gradient: {
        track: { bg: 'border.subtle' },
        range: { background: 'linear-gradient(0.25turn, #1484C9, #1EAB9E)' },
        thumb: { bg: 'bb.blue', borderColor: 'bb.blue' },
      },
    },
  },
});

/**
 * The portable fragment. Spread into a host's `createSystem(...).theme`.
 * Deliberately NOT wrapped in createSystem — see PORTABILITY RULES.
 */
export const BB_THEME_CONFIG = {
  semanticTokens,
  recipes: { button, heading },
  slotRecipes: { tabs, slider },
};
